import { Grid, Box } from '@mui/material';
import { FC, useRef } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { FormikErrors, FormikTouched } from 'formik';
import DropdownComponent from '../DropdownComponent';
import AppTimePicker from '../AppTimePicker';
import SmallTypography from '../SmallTypography';
import { OptionType } from '../../../utils/type';
import {
  getFinancialYearJulyFirstDate,
  getFinancialYearJuneEndDate,
} from '../../../utils/dateUtil';
import { GetTimeOffListType } from '../../../pages/profile/MyTimeOff';
import Textinput from '../Textinput';
import { formatDurationIntoDecimal } from '../../../utils/utilities';
import { DURATION_REGEX } from '../../../services/Constant';
import { ReactComponent as DeleteAlertIcon } from '../../../assets/images/deleteAlertIcon.svg';
import MediumTypography from '../MediumTypography';
import DatePickerComponent from '../DatePickerComponent';
interface TimeOffEntryProps {
  selectedDate: string | null;
  selectedYear: string;
  leave: GetTimeOffListType;
  index: number;
  leaveTypes: OptionType[];
  canEdit: boolean;
  errors: FormikErrors<{ appliedLeaves: GetTimeOffListType[] }>;
  touched: FormikTouched<{ appliedLeaves: GetTimeOffListType[] }>;
  onLeaveChange: (
    index: number,
    updatedLeave: Partial<GetTimeOffListType>,
  ) => void;
  onDeleteMyTimeOff: (leaveData: GetTimeOffListType, index: number) => void;
}

const TimeOffEntry: FC<TimeOffEntryProps> = ({
  selectedDate,
  selectedYear,
  leave,
  index,
  leaveTypes,
  canEdit,
  errors,
  touched,
  onLeaveChange,
  onDeleteMyTimeOff,
}) => {
  const ignoreEndDateNullRef = useRef(false);
  const isWeekendDate = (date: Dayjs) => {
    const day = date.day();
    return day === 0 || day === 6;
  };

  const countWeekdays = (startDate: string, endDate: string) => {
    let count = 0;
    let currentDate = dayjs(startDate, 'MM/DD/YYYY');
    const lastDate = dayjs(endDate, 'MM/DD/YYYY');

    while (!currentDate.isAfter(lastDate, 'day')) {
      if (!isWeekendDate(currentDate)) {
        count += 1;
      }
      currentDate = currentDate.add(1, 'day');
    }

    return count;
  };

  const getTotalDaysValue = (
    startDate: string | null,
    endDate: string | null,
  ) =>
    startDate && endDate ? countWeekdays(startDate, endDate).toString() : '';

  const handleTypeChange = (value: string) => {
    const selectedType = leaveTypes.find((type) => type.id === value);
    if (selectedType) {
      onLeaveChange(index, {
        typeId: selectedType.id,
        leaveName: selectedType.label,
      });
    } else {
      onLeaveChange(index, {
        typeId: '',
        leaveName: '',
      });
    }
  };

  const handleStartTimeChange = (text: dayjs.Dayjs | null) => {
    if (text === null) {
      onLeaveChange(index, {
        startTime: null,
        endTime: null,
      });
    } else {
      onLeaveChange(index, {
        startTime: `${dayjs().format('YYYY-MM-DD')}T${text.format('HH:mm')}`,
        endTime: null,
      });
      if (!leave.effectiveStartDate && selectedDate) {
        onLeaveChange(index, {
          effectiveStartDate: dayjs(selectedDate).format('MM/DD/YYYY'),
        });
      }
    }
  };

  const handleNoOfHrsChange = (value: string | null) => {
    onLeaveChange(index, {
      noOfHrs: value,
    });
  };

  const selectedDays =
    leave.effectiveStartDate && leave.effectiveEndDate
      ? countWeekdays(leave.effectiveStartDate, leave.effectiveEndDate)
      : null;
  const durationHours = Number(leave.noOfHrs);
  const totalHours =
    selectedDays !== null && !Number.isNaN(durationHours)
      ? Number((selectedDays * durationHours).toFixed(3))
      : null;

  return (
    <Grid
      item
      key={leave.id}
      direction={'row'}
      container
      columnSpacing={2}
      rowSpacing={2}
    >
      <Grid
        container
        item
        xs={12}
        columnSpacing={2}
        alignItems="center"
        wrap="nowrap"
      >
        <Grid item xs={0.6}>
          <Box
            sx={{
              width: '28px',
              height: '28px',
              borderRadius: '4px',
              border: '1px solid var(--thom-mono-gray)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#2A4241',
              fontFamily: 'Lato-Regular',
              fontSize: '12px',
              fontWeight: 600,
            }}
          >
            {index + 1}
          </Box>
        </Grid>
        <Grid item xs={3.8}>
          <DropdownComponent
            names={leaveTypes}
            labelid="MyTimeOffSelectType"
            defaultlabelid="Select Type"
            value={leave.typeId}
            disabled={!canEdit}
            handleChange={handleTypeChange}
          />
        </Grid>
        <Grid item xs={3.8}>
          <DatePickerComponent
            labelid="myTimeOffStartDate"
            disableFuture={false}
            defaultlabelid="Start Date*"
            shouldDisableDate={isWeekendDate}
            disabledDate
            minDate={
              leave.id === ''
                ? dayjs().startOf('week')
                : getFinancialYearJulyFirstDate(selectedYear)
            }
            maxDate={getFinancialYearJuneEndDate(selectedYear)}
            value={
              leave.effectiveStartDate === '' ||
              leave.effectiveStartDate === null
                ? null
                : dayjs(leave.effectiveStartDate)
            }
            handlechange={(value) => {
              const formattedDate = value
                ? dayjs(value).format('MM/DD/YYYY')
                : null;
              onLeaveChange(index, {
                effectiveStartDate: formattedDate,
                totalDays: getTotalDaysValue(
                  formattedDate,
                  leave.effectiveEndDate ?? null,
                ),
              });
            }}
          />
        </Grid>
        <Grid item xs={3.8}>
          <DatePickerComponent
            labelid="myTimeOffEndDate"
            disableFuture={false}
            defaultlabelid="End Date*"
            minDate={
              leave.effectiveStartDate
                ? dayjs(leave.effectiveStartDate)
                : getFinancialYearJulyFirstDate(selectedYear)
            }
            maxDate={getFinancialYearJuneEndDate(selectedYear)}
            shouldDisableDate={isWeekendDate}
            value={
              leave.effectiveEndDate === '' || leave.effectiveEndDate === null
                ? null
                : dayjs(leave.effectiveEndDate)
            }
            referenceDate={
              leave.effectiveStartDate
                ? dayjs(leave.effectiveStartDate)
                : getFinancialYearJulyFirstDate(selectedYear)
            }
            handlechange={(value) => {
              if (value === null) {
                if (ignoreEndDateNullRef.current) {
                  ignoreEndDateNullRef.current = false;
                  return;
                }
                onLeaveChange(index, {
                  effectiveEndDate: null,
                  totalDays: getTotalDaysValue(
                    leave.effectiveStartDate ?? null,
                    null,
                  ),
                });
              } else {
                const formattedDate = dayjs(value).format('MM/DD/YYYY');
                ignoreEndDateNullRef.current = true;
                setTimeout(() => {
                  ignoreEndDateNullRef.current = false;
                }, 200);
                console.log('TimeOffEntry endDate change', {
                  index,
                  rawValue: value,
                  formattedDate,
                });
                onLeaveChange(index, {
                  effectiveEndDate: formattedDate,
                  totalDays: getTotalDaysValue(
                    leave.effectiveStartDate ?? null,
                    formattedDate,
                  ),
                });
              }
            }}
          />
        </Grid>
      </Grid>
      <Grid container item xs={12} columnSpacing={2}>
        <Grid item xs={0.6} />
        <Grid item xs={3.8}>
          {errors.appliedLeaves &&
            touched.appliedLeaves &&
            (errors.appliedLeaves[index] as GetTimeOffListType)?.typeId && (
              <SmallTypography
                sxProps={{ color: 'red' }}
                labelId={
                  (errors.appliedLeaves[index] as GetTimeOffListType).typeId ??
                  ''
                }
                defaultLabelId="Field should not be empty"
              />
            )}
        </Grid>
        <Grid item xs={3.8}>
          {errors.appliedLeaves &&
            touched.appliedLeaves &&
            (errors.appliedLeaves[index] as GetTimeOffListType)
              ?.effectiveStartDate && (
              <SmallTypography
                sxProps={{ color: 'red' }}
                labelId={
                  (errors.appliedLeaves[index] as GetTimeOffListType)
                    .effectiveStartDate ?? ''
                }
              />
            )}
        </Grid>
        <Grid item xs={3.8}>
          {errors.appliedLeaves &&
            touched.appliedLeaves &&
            (errors.appliedLeaves[index] as GetTimeOffListType)
              ?.effectiveEndDate && (
              <SmallTypography
                sxProps={{ color: 'red' }}
                labelId={
                  (errors.appliedLeaves[index] as GetTimeOffListType)
                    .effectiveEndDate ?? ''
                }
              />
            )}
        </Grid>
      </Grid>
      <Grid
        container
        item
        xs={12}
        columnSpacing={2}
        alignItems="center"
        wrap="nowrap"
      >
        <Grid item xs={0.6} />
        <Grid item xs={3.8}>
          <AppTimePicker
            minuteStep={15}
            value={leave.startTime === null ? null : dayjs(leave.startTime)}
            onTimeChange={handleStartTimeChange}
            sxButtonProps={{
              padding: '16px 40px 16px 8px',
              width: '100%',
            }}
            name="Start Time*"
            disable={!canEdit}
            disableManualInput={!canEdit}
          />
        </Grid>
        {/* <Grid item xs={3.8}>
          {errors.appliedLeaves &&
            touched.appliedLeaves &&
            (errors.appliedLeaves[index] as GetTimeOffListType)?.startTime && (
              <SmallTypography
                sxProps={{ color: 'red' }}
                labelId={
                  (errors.appliedLeaves[index] as GetTimeOffListType).startTime ??
                  ''
                }
              />
            )}
        </Grid> */}
        <Grid item xs={3.8}>
          <Textinput
            name="noOfHrs"
            Value={leave.noOfHrs}
            labelid="myDuration"
            defaultlabelid="No. of Hours Per Day"
            inputProps={{
              maxLength: 5,
            }}
            handlechange={(value) => {
              if (DURATION_REGEX.test(value)) {
                handleNoOfHrsChange(value);
              }
            }}
            Required={true}
            onblur={() => {
              const formatted = formatDurationIntoDecimal(leave.noOfHrs);
              handleNoOfHrsChange(formatted);
            }}
          />
        </Grid>

        <Grid item xs={0.6} display="flex" justifyContent="flex-end">
          <Box sx={{ cursor: 'pointer' }}>
            <DeleteAlertIcon onClick={() => onDeleteMyTimeOff(leave, index)} />
          </Box>
        </Grid>
      </Grid>
      <Grid container item xs={12} columnSpacing={2}>
        <Grid item xs={0.6} />
        <Grid item xs={3.8}>
          {errors.appliedLeaves &&
            touched.appliedLeaves &&
            (errors.appliedLeaves[index] as GetTimeOffListType)?.startTime && (
              <SmallTypography
                sxProps={{ color: 'red' }}
                labelId={
                  (errors.appliedLeaves[index] as GetTimeOffListType)
                    .startTime ?? ''
                }
                defaultLabelId="Start Time is required"
              />
            )}
        </Grid>
        <Grid item xs={3.8}>
          {errors.appliedLeaves &&
            touched.appliedLeaves &&
            (errors.appliedLeaves[index] as GetTimeOffListType)?.noOfHrs && (
              <SmallTypography
                sxProps={{ color: 'red' }}
                labelId={
                  (errors.appliedLeaves[index] as GetTimeOffListType).noOfHrs ??
                  ''
                }
              />
            )}
        </Grid>
        <Grid item xs={3.8} />
        <Grid item xs={0.6} />
      </Grid>
      {selectedDays !== null && (
        <Grid container item xs={12} columnSpacing={2}>
          <Grid item xs={0.6} />
          <Grid item xs={3.8}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              <MediumTypography label="Number of days selected:" />
              <MediumTypography
                label={`${selectedDays}`}
                sxProps={{ color: 'var(--thom-green-primary)' }}
              />
            </Box>
          </Grid>
          <Grid item xs={3.8} />
          <Grid item xs={3.8} />
        </Grid>
      )}

      {totalHours !== null && (
        <Grid container item xs={12} columnSpacing={2}>
          <Grid item xs={0.6} />
          <Grid item xs={3.8}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              <MediumTypography label="Total Hours:" />
              <MediumTypography
                label={`${totalHours}`}
                sxProps={{ color: 'var(--thom-green-primary)' }}
              />
            </Box>
          </Grid>
          <Grid item xs={3.8} />
          <Grid item xs={3.8} />
        </Grid>
      )}
    </Grid>
  );
};

export default TimeOffEntry;
